import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SearchResult } from '../../api/models/search-result';
import { SearchUrlParameters } from '../../shared/model/search-url-parameters';
import { SearchEventTypeEnum } from '../../shared/model/search-event-type-enum';
import { SearchEventPublisher } from '../../shared/service/search-event-publisher.service';

export type NavigationItem = {
  value: number;
  isSelected: boolean;
};

@Component({
  selector: 'app-search-pagination',
  templateUrl: './search-pagination.component.html',
  styleUrls: ['./search-pagination.component.scss'],
})
export class SearchPaginationComponent implements OnChanges {
  @Input()
  searchResult!: SearchResult;

  @Input()
  searchParams: SearchUrlParameters = {};

  navigationItems: Array<NavigationItem> = [];

  showPagination: boolean = false;

  constructor(private searchEventPublisher: SearchEventPublisher) {}

  ngOnChanges(changes: SimpleChanges) {
    this.calculateNavigationItems();
  }

  navigate(modeOrPage: string | number): void {
    switch (modeOrPage) {
      case 'left':
        this.searchParams.pg = this.currentPage - 1;
        break;
      case 'right':
        this.searchParams.pg = this.currentPage + 1;
        break;
      default:
        this.searchParams.pg = modeOrPage as number;
    }

    this.searchEventPublisher.publishEvent(
      SearchEventTypeEnum.CHANGE_PAGE,
      this.searchParams
    );

    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  showNavigateLeft(): boolean {
    return this.currentPage > 1;
  }

  showNavigateRight(): boolean {
    return this.currentPage < this.totalPages;
  }

  get totalResults(): number {
    let totalResults = this.searchResult.pagination?.totalResults || 0;
    return this.hasEntityResult() ? totalResults + 1 : totalResults;
  }

  get interval(): string {
    let startIndex = this.searchResult.pagination?.startIndex;
    let endIndex = this.searchResult.pagination?.endIndex || 0;
    if (this.hasEntityResult()) {
      startIndex = startIndex || 1;
      endIndex += 1;
    }
    return startIndex === endIndex
      ? `${startIndex}`
      : `${startIndex} - ${endIndex}`;
  }

  get totalPages(): number {
    if (this.itemsPerPage == 0) {
      return 1;
    }

    const mod = this.totalResults % this.itemsPerPage;

    return mod != 0
      ? Math.trunc(this.totalResults / this.itemsPerPage) + 1
      : Math.trunc(this.totalResults / this.itemsPerPage);
  }

  get actualPage(): number {
    if (this.itemsPerPage == 0) {
      return 1;
    }

    return this.itemsPerPage == 1 ||
      this.itemsPerPage == this.startIndex ||
      this.startIndex % this.itemsPerPage == 0
      ? Math.trunc(this.startIndex / this.itemsPerPage)
      : Math.trunc(this.startIndex / this.itemsPerPage + 1);
  }

  private calculateNavigationItems(): void {
    if (this.totalPages <= 10) {
      this.navigationItems = this.createNavigationItems(
        1,
        this.totalPages,
        this.actualPage
      );
    } else {
      if (this.actualPage < 6) {
        this.navigationItems = this.createNavigationItems(
          1,
          10,
          this.actualPage
        );
      } else if (this.actualPage <= this.totalPages - 5) {
        this.navigationItems = this.createNavigationItems(
          this.actualPage - 5,
          this.actualPage + 5,
          this.actualPage
        );
      } else if (this.actualPage > this.totalPages - 5) {
        this.navigationItems = this.createNavigationItems(
          this.totalPages - 9,
          this.totalPages,
          this.actualPage
        );
      }
    }

    this.showPagination = this.totalPages > 1;
  }

  private hasEntityResult(): boolean {
    const firstElement = this.searchResult.searchResultList?.[0];
    return firstElement?.resourceType === 'search-entity';
  }

  private createNavigationItems(
    first: number,
    last: number,
    actualPage: number
  ): Array<NavigationItem> {
    let navigationItems: Array<NavigationItem> = [];

    for (let page = first; page <= last; page++) {
      navigationItems.push({
        value: page,
        isSelected: page == actualPage,
      });
    }

    return navigationItems;
  }

  private get currentPage(): number {
    return this.searchParams?.pg || 1;
  }

  private get itemsPerPage(): number {
    return this.searchParams?.ps || 10;
  }

  private get startIndex(): number {
    return this.searchResult?.pagination?.startIndex || 0;
  }
}
